// color-generator.ts (Enhanced)

import { ColorGeneratorOptions, ColorWithShades } from '@fleet/model';
import chroma from 'chroma-js';

export class ColorGenerator {
  private static readonly GOLDEN_ANGLE = 137.508;

  /**
   * Generates an array of colors, each with 5 distinct shades.
   * @param count - The number of base colors to generate.
   * @param options - Optional parameters to customize color generation.
   * @returns An array of ColorWithShades objects.
   */
  public static generateColorsWithShades(
    count: number,
    options?: ColorGeneratorOptions
  ): ColorWithShades[] {
    const colorsWithShades: ColorWithShades[] = [];

    // Generate distinct base colors by evenly distributing hues
    const baseColors = this.generateDistinctColors(count, options);

    // For each base color, generate 5 shades
    baseColors.forEach((baseColor) => {
      const shades = this.generateShades(baseColor, 5, options);
      colorsWithShades.push({ base: baseColor, shades });
    });

    return colorsWithShades;
  }

  public static warningColor(): ColorWithShades {
    const redBase = '#FF0000';
    const shades = this.generateShades(redBase, 5, {});
    return { base: redBase, shades: shades };
  }

  public static monoColor(): ColorWithShades {
    const grayBase = '#1e293b'; // darker still than #334155
    const shades = this.generateShades(grayBase, 5, {});
    return { base: grayBase, shades: shades };
  }

  /**
   * Generates distinct base colors by evenly distributing hues around the color wheel.
   * @param count - The number of base colors to generate.
   * @param options - Optional parameters to customize color generation.
   * @returns An array of HEX color strings.
   */
  private static generateDistinctColors(
    count: number,
    options?: ColorGeneratorOptions
  ): string[] {
    if (count === 0) return [];

    const {
      hueRange = [0, 360],
      saturation = 0.7,
      lightness = 0.5,
      includeNeutrals = false,
      colorBlindFriendly = false,
    } = options || {};

    // Handle single color case
    if (count === 1) {
      return [
        this.ensureVisibleColor(
          chroma
            .hsl((hueRange[0] + hueRange[1]) / 2, saturation, lightness)
            .hex()
        ),
      ];
    }

    const baseColors: string[] = [];
    const hueStep = (hueRange[1] - hueRange[0]) / count;
    const lightnessLevels = 5;

    for (let i = 0; i < count; i++) {
      let hue: number;
      let currentSaturation: number;
      let currentLightness: number;

      if (count > 20) {
        // Golden-angle hue spacing + lightness bands for large palettes
        hue = (i * this.GOLDEN_ANGLE) % 360;
        const itemsPerLightnessBand = Math.ceil(count / lightnessLevels);
        const lightnessBand = Math.floor(i / itemsPerLightnessBand);
        const maxBand = lightnessLevels - 1;
        currentLightness = 0.38 + (lightnessBand / maxBand) * 0.28;
        currentSaturation = 0.6 + (i % 3) * 0.05;
      } else {
        hue = hueRange[0] + hueStep * i;
        currentSaturation = saturation;
        currentLightness = lightness;
      }

      hue = hue % 360;

      if (colorBlindFriendly) {
        if ((hue >= 30 && hue <= 90) || (hue >= 150 && hue <= 210)) {
          hue = (hue + 15) % 360;
        }
      }

      baseColors.push(
        this.ensureVisibleColor(
          chroma.hsl(hue, currentSaturation, currentLightness).hex()
        )
      );
    }

    // Optionally, add neutral colors
    if (includeNeutrals) {
      const neutralCount = Math.min(5, count); // Limit the number of neutrals
      const neutrals = ['#f0f0f0', '#d9d9d9', '#bfbfbf', '#a6a6a6', '#8c8c8c'];
      for (let i = 0; i < neutralCount; i++) {
        if (baseColors.length >= count) break;
        baseColors.push(neutrals[i]);
      }
    }

    return baseColors;
  }

  private static ensureVisibleColor(hex: string): string {
    const color = chroma(hex);

    if (color.luminance() > 0.75) {
      return color.darken(1.5).hex();
    }

    if (color.luminance() < 0.08) {
      return color.brighten(0.8).hex();
    }

    return hex;
  }

  private static buildColorFromHue(
    hue: number,
    saturation: number,
    lightness: number,
    chromaScaleMode: ColorGeneratorOptions['chromaScaleMode']
  ): string {
    switch (chromaScaleMode) {
      case 'hsl':
        return chroma.hsl(hue, saturation, lightness).hex();
      case 'hsv':
        return chroma.hsv(hue, saturation, lightness).hex();
      case 'lch':
        return chroma.lch(lightness * 100, saturation * 100, hue).hex();
      case 'lab': {
        const chromaValue = saturation * 100;
        return chroma
          .lab(
            lightness * 100,
            chromaValue * Math.cos((hue * Math.PI) / 180),
            chromaValue * Math.sin((hue * Math.PI) / 180)
          )
          .hex();
      }
      case 'hcl':
      default:
        return chroma.hcl(lightness * 100, saturation * 100, hue).hex();
    }
  }

  /**
   * Generates shades for a given base color.
   * @param baseColor - The base color in HEX.
   * @param numberOfShades - Number of shades to generate.
   * @param options - Optional parameters to customize shade generation.
   * @returns An array of HEX shade strings.
   */
  private static generateShades(
    baseColor: string,
    numberOfShades: number,
    options?: ColorGeneratorOptions
  ): string[] {
    const shades: string[] = [];

    const { chromaScaleMode = 'hsl' } = options || {};

    // Define the scale from light to dark
    const scale = chroma
      .scale(['#ffffff', baseColor, '#000000'])
      .mode(chromaScaleMode);

    for (let i = 0; i < numberOfShades; i++) {
      const t = i / (numberOfShades - 1); // Normalize between 0 and 1
      const shade = scale(t).hex();
      shades.push(shade);
    }

    return shades;
  }
}

export function colourFromStatus(status: string): string {
  return textColourAndBackground(
    badgeColorFromStatus(status.toUpperCase()).badgeColor,
    true
  );
}

/**
 * Returns the Material icon name for a status. Use for timeline markers, badges, etc.
 * Mirrors the icon mapping previously on badgeColorFromStatus.
 */
export function iconFromStatus(status: string): string {
  if (!status) return 'info';
  switch (status) {
    case 'ACTIVE':
    case 'VALID':
    case 'IMPORTED':
    case 'FINISHED':
    case 'APPROVED':
    case 'PAID':
    case 'PUBLISHED':
    case 'STARTED':
    case 'SETTLED':
    case 'DISBURSED':
    case 'AVAILABLE':
    case 'COMPLETED':
    case 'DIRECT_DEBITED':
    case 'STICKY':
    case 'RESERVED':
      return 'done';

    case 'ON_LEAVE':
    case 'LEAVING_THE_NETWORK':
    case 'OUTSTANDING':
      return 'cottage';

    case 'PENDING_UNINSTALLATION':
    case 'EXEMPTED':
    case 'BANNED':
    case 'INVALID':
    case 'FAILED':
    case 'BLOCKED':
    case 'SETTLEMENT_REJECTED':
    case 'REJECTED':
    case 'DECLINED':
    case 'DIRECT_DEBIT_REJECTED':
    case 'VALIDATION_ERROR':
    case 'OVERDUE_RETURN':
    case 'SUSPENDED':
    case 'VERIFICATION_REQUIRED':
    case 'EXPIRED':
    case 'UNPUBLISHED':
    case 'ACKNOWLEDGEMENT_REQUIRED':
    case 'DISABLED':
    case 'DISBURSEMENTS_DISABLED':
    case 'LOST':
    case 'OVERDUE':
    case 'NO_REMITTANCE':
    case 'PENDING_PAYMENT':
    case 'LATE_COLLECTION':
    case 'SCHEDULED':
    case 'INACTIVE':
    case 'CANCELLED':
    case 'VOID':
    case 'PENDING_CLOSURE':
    case 'CLOSED':
      return 'block';

    case 'DELETED':
    case 'WRITTEN_OFF':
      return 'delete';

    case 'IN_ACTIVE':
    case 'OPEN':
    case 'INCOMPLETE':
    case 'ASSIGNED':
    case 'INFO_REQUIRED':
    case 'UNREGISTERED':
    case 'NOT_ATTEMPTED':
      return 'edit_note';

    case 'PROCESSED':
    case 'PROCESSING':
    case 'IN_PROGRESS':
    case 'VALIDATED':
    case 'VERIFIED':
    case 'DISPATCHED':
    case 'COLLECTED':
    case 'DRAFT':
    case 'OFFROAD':
    case 'PENDING_DEBT_COLLECTION':
    case 'ABANDONED':
    case 'PAYMENT_RETRY_REQUIRED':
    case 'INITIAL':
    case 'RETURNED':
      return 'info';

    case 'PENDING_REVIEW':
    case 'PENDING_INSTALLATION':
    case 'PENDING':
    case 'RESURRECTED':
    case 'PENDING_ACTIVATION':
      return 'schedule';

    case 'IN_REVIEW':
      return 'rate_review';

    case 'TRANSFERRED':
      return 'transfer_within_a_station';

    default:
      return 'info';
  }
}

export function badgeColorFromStatus(status: string, inverse?: boolean): any {
  if (status) {
    switch (status) {
      case 'ACTIVE':
        return inverse
          ? {
              textColor: 'text-green-100',
              bgColor: 'bg-green-600',
              badgeColor: 'green-600',
              textAndBgColor: 'bg-green-600 text-green-100',
            }
          : {
              textColor: 'text-green-700',
              bgColor: 'bg-green-50',
              badgeColor: 'green',
              textAndBgColor: 'bg-green-50 text-green-700',
            };

      case 'VALID':
      case 'IMPORTED':
      case 'FINISHED':
      case 'APPROVED':
      case 'PAID':
      case 'PUBLISHED':
      case 'STARTED':
      case 'SETTLED':
      case 'DISBURSED':
      case 'AVAILABLE':
      case 'COMPLETED':
      case 'DIRECT_DEBITED':
        return inverse
          ? {
              textColor: 'text-green-100',
              bgColor: 'bg-green-500',
              badgeColor: 'green-500',
              textAndBgColor: 'bg-green-500 text-green-100',
            }
          : {
              textColor: 'text-green-500',
              bgColor: 'bg-green-50',
              badgeColor: 'green-500',
              textAndBgColor: 'bg-green-50 text-green-500',
            };

      case 'RESERVED':
        return inverse
          ? {
              textColor: 'text-green-50',
              bgColor: 'bg-green-400',
              badgeColor: 'green-400',
              textAndBgColor: 'bg-green-400 text-green-50',
            }
          : {
              textColor: 'text-green-400',
              bgColor: 'bg-green-50',
              badgeColor: 'green-400',
              textAndBgColor: 'bg-green-50 text-green-400',
            };

      case 'RETURNED':
        return inverse
          ? {
              textColor: 'text-purple-100',
              bgColor: 'bg-purple-400',
              badgeColor: 'purple-400',
              textAndBgColor: 'bg-purple-400 text-purple-100',
            }
          : {
              textColor: 'text-purple-400',
              bgColor: 'bg-purple-50',
              badgeColor: 'purple-400',
              textAndBgColor: 'bg-purple-50 text-purple-400',
            };

      case 'BANNED':
      case 'INVALID':
      case 'FAILED':
      case 'BLOCKED':
      case 'SETTLEMENT_REJECTED':
      case 'REJECTED':
      case 'DECLINED':
      case 'DIRECT_DEBIT_REJECTED':
      case 'VALIDATION_ERROR':
      case 'OVERDUE_RETURN':
        return inverse
          ? {
              textColor: 'text-red-100',
              bgColor: 'bg-red-600',
              badgeColor: 'red-600',
              textAndBgColor: 'bg-red-600 text-red-100',
            }
          : {
              textColor: 'text-red-600',
              bgColor: 'bg-red-50',
              badgeColor: 'red-600',
              textAndBgColor: 'bg-red-50 text-red-600',
            };

      case 'SUSPENDED':
      case 'VERIFICATION_REQUIRED':
      case 'EXPIRED':
      case 'UNPUBLISHED':
      case 'ACKNOWLEDGEMENT_REQUIRED':
      case 'DISABLED':
      case 'DISBURSEMENTS_DISABLED':
      case 'LOST':
      case 'OVERDUE':
      case 'NO_REMITTANCE':
      case 'PENDING_PAYMENT':
      case 'LATE_COLLECTION':
      case 'SCHEDULED':
        return inverse
          ? {
              textColor: 'text-amber-100',
              bgColor: 'bg-amber-500',
              badgeColor: 'amber-500',
              textAndBgColor: 'bg-amber-500 text-amber-100',
            }
          : {
              textColor: 'text-amber-500',
              bgColor: 'bg-amber-50',
              badgeColor: 'amber-500',
              textAndBgColor: 'bg-amber-50 text-amber-500',
            };

      case 'PENDING_UNINSTALLATION':
      case 'LEAVING_THE_NETWORK':
      case 'OUTSTANDING':
        return inverse
          ? {
              textColor: 'text-pink-100',
              bgColor: 'bg-pink-400',
              badgeColor: 'pink-400',
              textAndBgColor: 'bg-pink-400 text-pink-100',
            }
          : {
              textColor: 'text-pink-700',
              bgColor: 'bg-pink-50',
              badgeColor: 'pink',
              textAndBgColor: 'bg-pink-50 text-pink-700',
            };

      case 'STICKY':
      case 'EXEMPTED':
        return inverse
          ? {
              textColor: 'text-yellow-100',
              bgColor: 'bg-yellow-400',
              badgeColor: 'yellow-400',
              textAndBgColor: 'bg-yellow-400 text-yellow-100',
            }
          : {
              textColor: 'text-yellow-700',
              bgColor: 'bg-yellow-50',
              badgeColor: 'yellow',
              textAndBgColor: 'bg-yellow-50 text-yellow-700',
            };

      case 'IN_ACTIVE':
      case 'OPEN':
      case 'INCOMPLETE':
      case 'ASSIGNED':
      case 'INFO_REQUIRED':
      case 'UNREGISTERED':
      case 'NOT_ATTEMPTED':
        return inverse
          ? {
              textColor: 'text-gray-300',
              bgColor: 'bg-gray-700',
              badgeColor: 'gray-700',
              textAndBgColor: 'bg-gray-700 text-gray-300',
            }
          : {
              textColor: 'text-gray-700',
              bgColor: 'bg-gray-50',
              badgeColor: 'gray',
              textAndBgColor: 'bg-gray-50 text-gray-700',
            };

      case 'INACTIVE':
      case 'ON_LEAVE':
      case 'CANCELLED':
        return inverse
          ? {
              textColor: 'text-gray-200',
              bgColor: 'bg-gray-500',
              badgeColor: 'gray-500',
              textAndBgColor: 'bg-gray-500 text-gray-200',
            }
          : {
              textColor: 'text-gray-500',
              bgColor: 'bg-gray-50',
              badgeColor: 'gray-500',
              textAndBgColor: 'bg-gray-50 text-gray-500',
            };

      case 'VOID':
        return inverse
          ? {
              textColor: 'text-gray-300',
              bgColor: 'bg-gray-700',
              badgeColor: 'gray-700',
              textAndBgColor: 'bg-gray-700 text-gray-300',
            }
          : {
              textColor: 'text-gray-700',
              bgColor: 'bg-gray-50',
              badgeColor: 'gray',
              textAndBgColor: 'bg-gray-50 text-gray-700',
            };

      case 'DELETED':
      case 'WRITTEN_OFF':
        return inverse
          ? {
              textColor: 'text-gray-100',
              bgColor: 'bg-gray-700',
              badgeColor: 'gray-700',
              textAndBgColor: 'bg-gray-700 text-gray-100',
            }
          : {
              textColor: 'text-gray-700',
              bgColor: 'bg-gray-50',
              badgeColor: 'gray',
              textAndBgColor: 'bg-gray-50 text-gray-700',
            };

      case 'PROCESSED':
      case 'PROCESSING':
      case 'IN_PROGRESS':
      case 'VALIDATED':
      case 'VERIFIED':
      case 'DISPATCHED':
      case 'COLLECTED':
      case 'DRAFT':
        return inverse
          ? {
              textColor: 'text-blue-100',
              bgColor: 'bg-blue-500',
              badgeColor: 'blue-500',
              textAndBgColor: 'bg-blue-500 text-blue-100',
            }
          : {
              textColor: 'text-blue-500',
              bgColor: 'bg-blue-50',
              badgeColor: 'blue-500',
              textAndBgColor: 'bg-blue-50 text-blue-500',
            };

      case 'PENDING_REVIEW':
      case 'PENDING_INSTALLATION':
      case 'PENDING':
        return inverse
          ? {
              textColor: 'text-blue-100',
              bgColor: 'bg-blue-500',
              badgeColor: 'blue-500',
              textAndBgColor: 'bg-blue-500 text-blue-100',
            }
          : {
              textColor: 'text-blue-500',
              bgColor: 'bg-blue-50',
              badgeColor: 'blue-500',
              textAndBgColor: 'bg-blue-50 text-blue-500',
            };

      case 'INITIAL':
        return inverse
          ? {
              textColor: 'text-blue-100',
              bgColor: 'bg-blue-700',
              badgeColor: 'blue-700',
              textAndBgColor: 'bg-blue-700 text-blue-100',
            }
          : {
              textColor: 'text-blue-700',
              bgColor: 'bg-blue-50',
              badgeColor: 'blue',
              textAndBgColor: 'bg-blue-50 text-blue-700',
            };

      case 'OFFROAD':
      case 'PENDING_DEBT_COLLECTION':
      case 'ABANDONED':
      case 'PAYMENT_RETRY_REQUIRED':
        return inverse
          ? {
              textColor: 'text-orange-100',
              bgColor: 'bg-orange-700',
              badgeColor: 'orange-700',
              textAndBgColor: 'bg-orange-700 text-orange-100',
            }
          : {
              textColor: 'text-orange-500',
              bgColor: 'bg-orange-50',
              badgeColor: 'orange-500',
              textAndBgColor: 'bg-orange-50 text-orange-500',
            };

      case 'RESURRECTED':
      case 'PENDING_ACTIVATION':
        return inverse
          ? {
              textColor: 'text-green-100',
              bgColor: 'bg-green-500',
              badgeColor: 'green-500',
              textAndBgColor: 'bg-green-500 text-green-100',
            }
          : {
              textColor: 'text-green-500',
              bgColor: 'bg-green-50',
              badgeColor: 'green-500',
              textAndBgColor: 'bg-green-50 text-green-500',
            };

      case 'IN_REVIEW':
        return inverse
          ? {
              textColor: 'text-orange-300',
              bgColor: 'bg-orange-700',
              badgeColor: 'orange-700',
              textAndBgColor: 'bg-orange-700 text-orange-300',
            }
          : {
              textColor: 'text-orange-500',
              bgColor: 'bg-orange-50',
              badgeColor: 'orange-500',
              textAndBgColor: 'bg-orange-50 text-orange-500',
            };

      case 'PENDING_CLOSURE':
      case 'CLOSED':
        return inverse
          ? {
              textColor: 'text-white',
              bgColor: 'bg-black',
              badgeColor: 'black',
              textAndBgColor: 'bg-black text-white',
            }
          : {
              textColor: 'text-black',
              bgColor: 'bg-white',
              badgeColor: 'black',
              textAndBgColor: 'bg-white text-black',
            };

      default:
        return inverse
          ? {
              textColor: 'text-white',
              bgColor: 'bg-black',
              badgeColor: 'black',
              textAndBgColor: 'bg-black text-white',
            }
          : {
              textColor: 'text-black',
              bgColor: 'bg-white',
              badgeColor: 'black',
              textAndBgColor: 'bg-white text-black',
            };
    }
  }
  return null;
}

/** fleet-badge outline palette used by medallion-style badges */
export type FleetMedallionOutlineBadgeColor =
  | 'blue'
  | 'purple'
  | 'yellow'
  | 'orange'
  | 'red';

/**
 * Outline badge color from medallion type (first letter), matching fleet-medallion-badge.
 * P → purple, K → yellow, T → orange, R → red, default → blue.
 */
export function badgeColorFromMedallionType(
  type: string | null | undefined
): FleetMedallionOutlineBadgeColor {
  const prefix = String(type ?? '').slice(0, 1);
  switch (prefix) {
    case 'P':
      return 'purple';
    case 'K':
      return 'yellow';
    case 'T':
      return 'orange';
    case 'R':
      return 'red';
    default:
      return 'blue';
  }
}

/**
 * Returns Tailwind classes for a dark pill/circle (bg + text) that match fleet-badge
 * when used with variationConfigWithStatus and dark: true.
 * Use this so timeline markers and other UI match the badge colour.
 */
export function darkPillClassesFromBadgeColor(badgeColor: string): string {
  if (!badgeColor) return 'bg-gray-700 text-white';
  if (/^#[0-9A-F]{6}$/i.test(badgeColor)) {
    return `bg-[${badgeColor}] text-white`;
  }
  const defaultDark = 'bg-' + badgeColor + ' text-white';
  switch (badgeColor) {
    case 'green':
      return 'bg-green-700 text-white';
    case 'red':
      return 'bg-red-700 text-white';
    case 'yellow':
      return 'bg-yellow-700 text-white';
    case 'gray':
      return 'bg-gray-700 text-white';
    case 'gray-500':
      return 'bg-gray-500 text-white';
    case 'light-gray':
      return 'bg-gray-600 text-white';
    case 'blue':
      return 'bg-blue-700 text-white';
    case 'blue-500':
      return 'bg-blue-500 text-white';
    case 'indigo':
      return 'bg-indigo-700 text-white';
    case 'purple':
      return 'bg-purple-700 text-white';
    case 'pink':
      return 'bg-pink-700 text-white';
    case 'orange':
      return 'bg-orange-700 text-white';
    case 'orange-500':
      return 'bg-orange-500 text-white';
    case 'orange-700':
      return 'bg-orange-700 text-white';
    case 'amber':
      return 'bg-amber-700 text-white';
    case 'amber-500':
      return 'bg-amber-500 text-white';
    case 'emerald':
      return 'bg-emerald-700 text-white';
    case 'emerald-500':
      return 'bg-emerald-500 text-white';
    case 'lime-500':
      return 'bg-lime-500 text-white';
    case 'green-500':
      return 'bg-green-500 text-white';
    case 'green-400':
      return 'bg-green-400 text-white';
    case 'red-600':
      return 'bg-red-600 text-white';
    case 'purple-400':
      return 'bg-purple-400 text-white';
    case 'pink-400':
      return 'bg-pink-400 text-white';
    case 'yellow-400':
      return 'bg-yellow-400 text-white';
    case 'blue-700':
      return 'bg-blue-700 text-white';
    case 'black':
      return 'bg-black text-white';
    default:
      return defaultDark;
  }
}
export function textColourAndBackground(color: string, dark: boolean) {
  if (/^#[0-9A-F]{6}$/i.test(color)) {
    return dark
      ? `bg-[${color}] text-white`
      : `bg-[${color}-100] text-[${color}]`;
  }

  const defaultDark = 'bg-' + color + ' text-white';
  const colourWithoutNumber = color.replace(/[-\d]/g, '');
  const defaultLight =
    'bg-' +
    colourWithoutNumber +
    '-100' +
    ' ' +
    'text-' +
    colourWithoutNumber +
    '-700';
  switch (color) {
    case 'green':
      return dark ? 'bg-green-700 text-white' : 'bg-green-100 text-green-700';
    case 'red':
      return dark ? 'bg-red-700 text-white' : 'bg-red-100 text-red-700';
    case 'yellow':
      return dark
        ? 'bg-yellow-700 text-white'
        : 'bg-yellow-100 text-yellow-700';
    case 'gray':
      return dark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700';
    case 'gray-500':
      return dark ? 'bg-gray-500 text-white' : 'bg-gray-100 text-gray-500';
    case 'light-gray':
      return dark ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-500';
    case 'blue':
      return dark ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-700';
    case 'blue-500':
      return dark ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-500';

    case 'indigo':
      return dark
        ? 'bg-indigo-700 text-white'
        : 'bg-indigo-100 text-indigo-700';
    case 'purple':
      return dark
        ? 'bg-purple-700 text-white'
        : 'bg-purple-100 text-purple-700';
    case 'pink':
      return dark ? 'bg-pink-700 text-white' : 'bg-pink-100 text-pink-700';
    case 'orange':
      return dark
        ? 'bg-orange-700 text-white'
        : 'bg-orange-100 text-orange-700';
    case 'orange-500':
      return dark ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-500';
    case 'amber':
      return dark ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-700';
    case 'amber-500':
      return dark ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-500';
    case 'emerald':
      return dark
        ? 'bg-emerald-700 text-white'
        : 'bg-emerald-100 text-emerald-700';
    case 'emerald-500':
      return dark
        ? 'bg-emerald-500 text-white'
        : 'bg-emerald-50 text-emerald-500';
    case 'lime-500':
      return dark ? 'bg-lime-500 text-white' : 'bg-lime-50 text-lime-500';

    case 'green-500':
      return dark ? 'bg-green-500 text-white' : 'bg-green-50 text-green-500';
    case 'red-600':
      return dark ? 'bg-red-600 text-white' : 'bg-red-50 text-red-600';
    default:
      return dark ? defaultDark : defaultLight;
  }
}
